import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { CalendarDays, Link, Unlink, RefreshCw } from "lucide-react";
import { customFetch, ApiError } from "@workspace/api-client-react";

export function GoogleCalendarSync() {
  const { toast } = useToast();
  const [status, setStatus] = useState<{ googleConfigured: boolean; connected: boolean } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleOAuthRedirect = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const gcalResult = params.get("google_calendar");
    if (gcalResult) {
      params.delete("google_calendar");
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      if (gcalResult === "connected") {
        toast({ title: "Google Calendar Connected", description: "Your Google Calendar is now linked. You can sync your sessions." });
      } else if (gcalResult === "error") {
        toast({ title: "Connection Failed", description: "Could not connect Google Calendar. Please try again.", variant: "destructive" });
      }
    }
  }, [toast]);

  useEffect(() => {
    handleOAuthRedirect();
    fetchStatus();
  }, [handleOAuthRedirect]);

  async function fetchStatus() {
    try {
      const data = await customFetch<{ googleConfigured: boolean; connected: boolean }>("/api/calendar/google/status");
      setStatus(data);
    } catch {
    } finally {
      setLoading(false);
    }
  }

  async function handleConnect() {
    try {
      const { url } = await customFetch<{ url: string }>("/api/calendar/google/auth-url");
      window.location.href = url;
    } catch {
      toast({ title: "Google Calendar not available", description: "The admin has not configured Google Calendar integration.", variant: "destructive" });
    }
  }

  async function handleDisconnect() {
    try {
      await customFetch("/api/calendar/google/disconnect", {
        method: "POST",
      });
      setStatus((prev) => prev ? { ...prev, connected: false } : prev);
      toast({ title: "Disconnected", description: "Google Calendar has been disconnected." });
    } catch {
      toast({ title: "Error", description: "Failed to disconnect.", variant: "destructive" });
    }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const data = await customFetch<{ synced: number; total: number }>("/api/calendar/google/sync", {
        method: "POST",
      });
      toast({ title: "Synced!", description: `${data.synced} of ${data.total} sessions added to Google Calendar.` });
    } catch (err: unknown) {
      let message = "Failed to sync with Google Calendar.";
      if (err instanceof ApiError && err.data && typeof err.data === "object") {
        const data = err.data as Record<string, unknown>;
        if (typeof data.error === "string") {
          message = data.error;
        }
      }
      toast({ title: "Sync failed", description: message, variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  }

  if (loading || !status) return null;
  if (!status.googleConfigured) return null;

  if (!status.connected) {
    return (
      <Button variant="outline" size="sm" className="border-white/10 hover:bg-white/5" onClick={handleConnect}>
        <Link className="w-4 h-4 mr-2" /> Connect Google Calendar
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2 items-center">
        <Button variant="outline" size="sm" className="border-white/10 hover:bg-white/5" onClick={handleSync} disabled={syncing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync to Google Calendar"}
        </Button>
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-400" onClick={handleDisconnect}>
          <Unlink className="w-4 h-4 mr-1" /> Disconnect
        </Button>
      </div>
      <p className="text-xs text-muted-foreground pl-1">Google Calendar events are used to block your availability.</p>
    </div>
  );
}
